// Playable mech definitions. Movement profiles are the primary "feel" knobs.

import { COLORS } from '../config/palette';
import type { MechDef, MechId, StatBlock } from '../core/types';

export const BASE_STATS: StatBlock = {
  maxHp: 100,
  maxShield: 40,
  shieldRegen: 20,
  shieldDelay: 3,
  armor: 0,
  moveSpeed: 1,
  jumpPower: 1,
  airJumps: 1,
  airDashes: 1,
  dashCooldown: 1,
  dashPower: 1,
  damage: 1,
  fireRate: 1,
  heatCapacity: 1,
  cooling: 1,
  critChance: 0.05,
  critMult: 1.6,
  signatureCooldown: 1,
  ultimateCharge: 1,
  chainBonus: 0,
  chainDamage: 1,
  burnPower: 1,
  explosionRadius: 1,
  explosionDamage: 1,
  knockback: 1,
  knockbackResist: 0,
  pickupRadius: 150,
  scrapGain: 1,
  damageTaken: 1,
};

export const MECHS: Record<MechId, MechDef> = {
  volt: {
    id: 'volt',
    name: 'VOLT',
    tagline: 'Lightning in a chassis.',
    playstyle: 'Fast movement and close-range aggression',
    reactorColor: COLORS.volt,
    glowColor: COLORS.voltGlow,
    base: { ...BASE_STATS },
    movement: {
      runSpeed: 430,
      groundAccel: 4600,
      groundDecel: 5600,
      airAccel: 3200,
      airDecel: 1800,
      jumpVelocity: 990,
      airJumpVelocity: 900,
      gravity: 2650,
      fallGravityMult: 1.45,
      apexGravityMult: 0.55,
      apexThreshold: 140,
      maxFall: 1550,
      jumpCutMult: 0.42,
      coyoteTime: 0.1,
      jumpBuffer: 0.12,
      dashSpeed: 1450,
      dashDuration: 0.15,
      dashIFrames: 0.14,
      dashRecovery: 0.09,
      dashCooldown: 0.5,
    },
    passive: 'live_wire',
    passiveName: 'Live Wire',
    passiveDesc: '+25% damage to enemies within close range. Kills refund 40% of the dash cooldown.',
    signature: 'arc_dash',
    signatureName: 'Arc Dash',
    signatureDesc:
      'Lunge toward the cursor through enemies, marking each one. Electricity then chains between every marked target.',
    signatureCooldown: 6,
    ultimate: 'overload',
    ultimateName: 'Overload',
    ultimateDesc:
      'Discharge the reactor: lightning arcs between every enemy on screen in repeating waves while VOLT moves faster.',
    startingWeapon: 'pulse_carbine',
    kitTags: ['electric', 'dash'],
  },
  bastion: {
    id: 'bastion',
    name: 'BASTION',
    tagline: 'An immovable object with a cannon.',
    playstyle: 'Heavy firepower and defensive positioning',
    reactorColor: COLORS.bastion,
    // The heavy frame towers over the others (its sheet is baked to fit the 128 px cell).
    viewScale: 1.3,
    glowColor: COLORS.bastionGlow,
    base: {
      ...BASE_STATS,
      maxHp: 160,
      maxShield: 80,
      shieldRegen: 26,
      shieldDelay: 3.2,
      armor: 0.1,
      knockbackResist: 0.5,
      critChance: 0.04,
    },
    movement: {
      runSpeed: 330,
      groundAccel: 3000,
      groundDecel: 4200,
      airAccel: 2000,
      airDecel: 1200,
      jumpVelocity: 930,
      airJumpVelocity: 820,
      gravity: 2800,
      fallGravityMult: 1.35,
      apexGravityMult: 0.7,
      apexThreshold: 120,
      maxFall: 1700,
      jumpCutMult: 0.5,
      coyoteTime: 0.1,
      jumpBuffer: 0.12,
      dashSpeed: 1150,
      dashDuration: 0.17,
      dashIFrames: 0.12,
      dashRecovery: 0.14,
      dashCooldown: 0.8,
    },
    passive: 'bulwark_frame',
    passiveName: 'Bulwark Frame',
    passiveDesc: 'Standing still for 0.6s braces the frame: -30% damage taken and shields regenerate twice as fast.',
    signature: 'siege_field',
    signatureName: 'Siege Field',
    signatureDesc:
      'Deploy a barrier toward the cursor. It blocks enemy fire, and your shots passing through it are amplified.',
    signatureCooldown: 10,
    ultimate: 'fortress_protocol',
    ultimateName: 'Fortress Protocol',
    ultimateDesc: 'Anchor in place and call a sustained artillery barrage that follows the cursor. Immune to knockback.',
    startingWeapon: 'scatterforge',
    kitTags: ['shield', 'kinetic'],
    unlock: 'mech:bastion',
  },
  wraith: {
    id: 'wraith',
    name: 'WRAITH',
    tagline: 'Gone before the echo lands.',
    playstyle: 'Aerial movement and precise burst damage',
    reactorColor: COLORS.wraith,
    // Tall, long-legged frame: baked smaller to fit the cell, shown a little larger.
    viewScale: 1.15,
    glowColor: COLORS.wraithGlow,
    base: {
      ...BASE_STATS,
      maxHp: 85,
      maxShield: 35,
      shieldRegen: 18,
      shieldDelay: 2.6,
      airJumps: 2,
      airDashes: 2,
      critChance: 0.12,
      critMult: 1.8,
    },
    movement: {
      runSpeed: 390,
      groundAccel: 4000,
      groundDecel: 5000,
      airAccel: 3600,
      airDecel: 2200,
      jumpVelocity: 960,
      airJumpVelocity: 860,
      gravity: 2400,
      fallGravityMult: 1.25,
      apexGravityMult: 0.45,
      apexThreshold: 170,
      maxFall: 1300,
      jumpCutMult: 0.45,
      coyoteTime: 0.11,
      jumpBuffer: 0.12,
      dashSpeed: 1350,
      dashDuration: 0.16,
      dashIFrames: 0.16,
      dashRecovery: 0.08,
      dashCooldown: 0.6,
      glideFallSpeed: 260,
    },
    passive: 'ghost_drive',
    passiveName: 'Ghost Drive',
    passiveDesc: 'Two air jumps and two air dashes. Hold jump while falling to glide. +20% damage while airborne.',
    signature: 'phase_echo',
    signatureName: 'Phase Echo',
    signatureDesc:
      'Phase out of reality, passing through danger, and leave an echo that repeats your attacks at reduced power.',
    signatureCooldown: 7,
    ultimate: 'eclipse',
    ultimateName: 'Eclipse',
    ultimateDesc:
      'Distort time: enemies and their projectiles crawl at 30% speed while four echoes orbit WRAITH and mirror every shot.',
    startingWeapon: 'rail_driver',
    kitTags: ['precision', 'dash'],
    unlock: 'mech:wraith',
  },
};

export const MECH_ORDER: readonly MechId[] = ['volt', 'bastion', 'wraith'];
